import React, { ReactNode, useState } from "react";
import {useTranslation} from "react-i18next";
import Competition from "model/Competition";
import { useQuery } from "react-query";
import getQuestions from "api/getQuestions";
import Form from "@awsui/components-react/form";
import SpaceBetween from "@awsui/components-react/space-between";
import Button from "@awsui/components-react/button";
import FormField from "@awsui/components-react/form-field";
import RadioGroup from "@awsui/components-react/radio-group";
import Question from "model/Question";
import Spinner from "@awsui/components-react/spinner";
import { Flashbar, FlashbarProps, Header, IconProps } from "@awsui/components-react";
import SubmitAnswersButton from "pages/questions/components/SubmitAnswersButton";
import { useAuth } from "hooks/useAuth";
import SubmitFlashbar from "components/SubmitFlashbar";


interface QuestionsFormProps {
    competition: Competition;
}

const QuestionsForm: React.FC<QuestionsFormProps> = (props) => {

    const {t} = useTranslation(["questions", "common", "errors"]);
    const competition = props.competition;

    const [{ league, user }] = useAuth();

    // <LeagueRoute> wraps /questions in App.tsx, so league is always defined here.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const leagueForApi = league!;

    const [answers, setAnswers] = useState<Map<number, number>>(new Map());
    const [buttonEnabled, setButtonEnabled] = useState(true);
    const [submitMessage, setSubmitMessage] = useState<FlashbarProps.MessageDefinition>();
    const [buttonIconName, setButtonIconName] = useState<IconProps.Name | undefined>();

    const {
        isLoading: questionsIsLoading,
        data: questions,
        error: getQuestionsError
    } = useQuery<Question[], Error>(`getQuestions-${competition.code}`, () => getQuestions(competition, leagueForApi, user?.token), {
        onSuccess: (returnedQuestions: Question[]) => {
            returnedQuestions.filter(question => {
                return (question.answers.filter(answer => answer.selected).length === 1);
            }).forEach(question => {
                changeAnswers(
                    question.questionId,
                    question.answers.filter(answer => answer.selected).map(answer => answer.answerId)[0]
                );
            });
        }
    });

    const changeAnswers = (questionId: number, answerId: number): void => {
        setAnswers(new Map(answers.set(questionId, answerId)));
        setButtonEnabled(true);
        setButtonIconName(undefined);
    };

    const getQuestionsAsFormFields = (questions: Question[]): ReactNode[] => {
        return questions.map(question => (
            <FormField key={question.questionId} label={<Header variant="h2">{question.text}</Header>}>
                <RadioGroup
                    onChange={({ detail }) => changeAnswers(question.questionId, parseInt(detail.value, 10))}
                    value={`${answers.get(question.questionId)}`}
                    items={question.answers.map(answer => ({
                        label: answer.text,
                        value: `${answer.answerId}`
                    }))}
                />
            </FormField>
        ));
    };

    if (getQuestionsError) {
        return (
            <Flashbar
                items={[{
                    header: t("errors:questionsLoad"),
                    type: "error",
                    content: t("common:errors.tryReload"),
                    dismissible: false,
                }]}
            />
        );
    }

    if (questionsIsLoading || !questions) {
        return (
            <Spinner />
        );
    }

    return (
        <form onSubmit={e => e.preventDefault()}>
            <Form
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button formAction="none" variant="link" href="/">
                            {t("common:buttons.cancel")}
                        </Button>
                        <SubmitAnswersButton
                            selectedAnswers={answers}
                            numberOfQuestions={questions.length}
                            competition={competition}
                            buttonEnabled={buttonEnabled}
                            setButtonEnabled={setButtonEnabled}
                            setSubmitMessage={setSubmitMessage}
                            buttonIconName={buttonIconName}
                            setButtonIconName={setButtonIconName}
                        />
                    </SpaceBetween>
                }
            >
                <SpaceBetween direction="vertical" size="l">
                    {submitMessage && (
                        <SubmitFlashbar
                            submitMessage={submitMessage}
                            setSubmitMessage={setSubmitMessage}
                        />
                    )}
                    {getQuestionsAsFormFields(questions)}
                </SpaceBetween>
            </Form>
        </form>
    );

};

export default QuestionsForm;
